/**
 * Fleet Performance Report Preview Page
 *
 * Standalone page component rendered at /tracking/reports/fleet-performance-preview.
 * Wraps the FleetPerformanceReport output view with print/close actions,
 * consistent with the PrintPreviewPage pattern used by other report types.
 *
 * Requirements: 6.1, 6.2
 */

import React from "react";
import { useLocation } from "react-router-dom";
import FleetPerformanceReportWithBoundary from "./index";
import { getFleetPerformanceData } from "./services";
import { sortVehicles, formatValue, formatPoint, formatString } from "./FleetPerformanceTable";
import "./FleetPerformancePreviewPage.scss";

/**
 * Generates a CSV string from fleet performance data.
 * Includes both Best Of 5 and Worst of 5 tables separated by an empty row.
 */
const generateCSV = (data) => {
  const headers = [
    "Rank",
    "Total Point",
    "Plate No.",
    "Vehicle Type",
    "Fuel Economy (Km/L)",
    "Fuel Economy (Point)",
    "Idling (%)",
    "Idling (Point)",
    "CO2 Emissions (mt)",
    "CO2 Emissions (Point)",
    "Risk Rating (Rating)",
    "Risk Rating (Point)",
  ];

  const vehicleToRow = (vehicle, index) => {
    return [
      index + 1,
      formatPoint(vehicle.total_point),
      formatString(vehicle.plate_number),
      formatString(vehicle.vehicle_type),
      formatValue(vehicle.fuel_economy_value, 2),
      formatPoint(vehicle.fuel_economy_point),
      formatValue(vehicle.idling_percentage, 1),
      formatPoint(vehicle.idling_point),
      formatValue(vehicle.co2_emission_value, 3),
      formatPoint(vehicle.co2_emission_point),
      formatString(vehicle.risk_rating_label),
      formatPoint(vehicle.risk_rating_point),
    ];
  };

  const escapeCsvValue = (val) => {
    const str = String(val);
    if (str.includes(",") || str.includes('"') || str.includes("\n")) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const rows = [];

  // Best Of 5 section
  rows.push(["Best Of 5 Vehicle"]);
  rows.push(headers);
  const topVehicles = sortVehicles(data.top_vehicles || [], "desc");
  topVehicles.forEach((v, i) => rows.push(vehicleToRow(v, i)));

  // Separator
  rows.push([]);

  // Worst of 5 section
  rows.push(["Worst of 5 Vehicle"]);
  rows.push(headers);
  const worstVehicles = sortVehicles(data.worst_vehicles || [], "asc");
  worstVehicles.forEach((v, i) => rows.push(vehicleToRow(v, i)));

  return rows.map((row) => row.map(escapeCsvValue).join(",")).join("\n");
};

/**
 * Triggers a browser download of the given content as a file.
 */
const downloadFile = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const FleetPerformancePreviewPage = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [downloading, setDownloading] = React.useState(false);

  // Extract optional parameters from URL query string
  const reportParams = {
    startTime: params.get("startTime") || undefined,
    endTime: params.get("endTime") || undefined,
    timezone: params.get("timezone") || undefined,
  };

  const handlePrint = () => {
    window.print();
  };

  const handleClose = () => {
    window.close();
  };

  const handleDownloadCSV = async () => {
    try {
      setDownloading(true);
      const data = await getFleetPerformanceData(reportParams);
      const csv = generateCSV(data);
      const timestamp = new Date().toISOString().slice(0, 10);
      downloadFile(csv, `fleet-performance-report_${timestamp}.csv`, "text/csv;charset=utf-8;");
    } catch (err) {
      console.error("Failed to download CSV:", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fleet-performance-preview-page">
      {/* Header with action buttons - hidden during print */}
      <div className="fleet-performance-preview-header no-print">
        <div className="header-left">
          <h2 className="app-title">Fleet Performance Report</h2>
        </div>
        <div className="header-right">
          <div className="header-actions">
            <button onClick={handleDownloadCSV} className="btn-download" disabled={downloading}>
              <svg
                className="icon"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {downloading ? "Downloading..." : "Download CSV"}
            </button>
            <button onClick={handlePrint} className="btn-print">
              <svg
                className="icon"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6 14h12v8H6v-8z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Print
            </button>
            <button onClick={handleClose} className="btn-close">
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Report content */}
      <div className="fleet-performance-preview-content">
        <FleetPerformanceReportWithBoundary params={reportParams} />
      </div>
    </div>
  );
};

export default FleetPerformancePreviewPage;
